// =========================
// FeatureManagementPanel — PLATFORM-003 (Admin operational control plane)
//
// The Admin application's Feature Management section. Browse + search every
// runtime feature; view its status, audience, lifecycle stage, dependencies,
// required migrations, and deployment/verification/legal readiness; enable /
// disable it at runtime; and view its immutable audit history.
//
// The server is the enablement authority: this panel DISPLAYS the server-computed
// enablement warnings (enablement.reasons) and prevents the invalid operation in
// the UI (disabled Enable button) — but even if the button were forced, the
// admin-set-feature-state endpoint independently refuses (409) an enable whose
// prerequisites are unmet. Reads/writes through lib/admin.
//
// Rendered as a tab inside AdminDashboard; route gating (ProtectedRoute >
// AdminRoute) + the admin-* function guard handle auth/admin before mount.
// =========================

import React, { useEffect, useState, useCallback } from 'react';
import { listFeatureRegistry, setFeatureState, listFeatureAudit } from '../../lib/admin';
import {
  audienceLabel,
  lifecycleLabel,
  prerequisiteWarnings,
  canOfferEnable,
} from '../../lib/featureRegistry';

export default function FeatureManagementPanel() {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [actionError, setActionError] = useState(null);
  const [auditFor, setAuditFor] = useState(null);
  const [auditRows, setAuditRows] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: loadError } = await listFeatureRegistry();
    if (loadError) {
      setError(loadError.message || 'Could not load the feature registry');
      setFeatures([]);
    } else {
      setFeatures(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleToggle(flag) {
    setActionError(null);
    const { error: toggleError } = await setFeatureState({
      flag_key: flag.flag_key,
      enabled: !flag.enabled,
    });
    if (toggleError) {
      const reasons = toggleError.reasons ? ` — ${toggleError.reasons.join('; ')}` : '';
      setActionError(`${toggleError.message}${reasons}`);
      return;
    }
    await load();
  }

  async function handleHistory(flag) {
    if (auditFor === flag.flag_key) {
      setAuditFor(null);
      setAuditRows([]);
      return;
    }
    const { data } = await listFeatureAudit({ flag_key: flag.flag_key });
    setAuditFor(flag.flag_key);
    setAuditRows(data || []);
  }

  if (loading) {
    return (
      <div style={{ padding: 'var(--space-5)', color: 'var(--color-steel-lt)', fontSize: 'var(--text-base)' }}>
        Loading…
      </div>
    );
  }

  if (error) {
    return (
      <div
        role="alert"
        style={{
          padding: 'var(--space-4) var(--space-5)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-danger)',
          background: 'var(--color-danger-ghost)',
          color: 'var(--color-danger-lt)',
          fontWeight: 'var(--weight-semibold)',
        }}
      >
        {error}
      </div>
    );
  }

  const q = query.trim().toLowerCase();
  const visible = q
    ? features.filter(
        (fl) =>
          fl.flag_key.toLowerCase().includes(q) ||
          (fl.display_name || '').toLowerCase().includes(q) ||
          (fl.category || '').toLowerCase().includes(q)
      )
    : features;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <input
        type="search"
        aria-label="Search features"
        placeholder="Search features…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        style={{
          padding: 'var(--space-3) var(--space-4)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-steel-dk)',
          background: 'var(--color-navy-lt)',
          color: 'var(--color-white)',
          fontSize: 'var(--text-base)',
        }}
      />

      {actionError ? (
        <div
          role="alert"
          style={{
            padding: 'var(--space-3) var(--space-4)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-danger)',
            background: 'var(--color-danger-ghost)',
            color: 'var(--color-danger-lt)',
            fontSize: 'var(--text-sm)',
          }}
        >
          {actionError}
        </div>
      ) : null}

      {visible.length === 0 ? (
        <div style={{ padding: 'var(--space-5)', color: 'var(--color-muted)', fontStyle: 'italic' }}>
          No features match your search
        </div>
      ) : (
        visible.map((flag) => {
          const warnings = prerequisiteWarnings(flag);
          const offerEnable = canOfferEnable(flag);
          const deps = flag.dependencies || [];
          const migrations = flag.required_migrations || [];
          return (
            <div key={flag.id || flag.flag_key} className="card" style={{ display: 'grid', gap: 'var(--space-3)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                  <span style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--text-lg)', fontWeight: 'var(--weight-bold)', color: 'var(--color-white)' }}>
                    {flag.display_name || flag.flag_key}
                  </span>
                  <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-steel)' }}>
                    {flag.flag_key}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                  <span className={`badge ${flag.enabled ? 'badge-active' : 'badge-pass'}`}>
                    {flag.enabled ? 'ENABLED' : 'DISABLED'}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleToggle(flag)}
                    disabled={!flag.enabled && !offerEnable}
                    title={!flag.enabled && !offerEnable ? 'Prerequisites not satisfied' : undefined}
                    className={flag.enabled ? 'btn btn-secondary' : 'btn btn-primary'}
                  >
                    {flag.enabled ? 'Disable' : 'Enable'} {flag.flag_key}
                  </button>
                  <button type="button" onClick={() => handleHistory(flag)} className="btn btn-ghost">
                    History {flag.flag_key}
                  </button>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', fontSize: 'var(--text-sm)' }}>
                <span className="badge">{audienceLabel(flag.audience)}</span>
                <span className="badge">{lifecycleLabel(flag.lifecycle_stage)}</span>
                {flag.owner ? <span style={{ color: 'var(--color-steel)' }}>owner: {flag.owner}</span> : null}
                <span style={{ color: flag.deployment_complete ? 'var(--color-steel-lt)' : 'var(--color-warning)' }}>
                  deploy: {flag.deployment_complete ? 'complete' : 'pending'}
                </span>
                <span style={{ color: flag.verification_complete ? 'var(--color-steel-lt)' : 'var(--color-warning)' }}>
                  verify: {flag.verification_complete ? 'complete' : 'pending'}
                </span>
              </div>

              {(deps.length > 0 || migrations.length > 0) ? (
                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-steel)' }}>
                  {migrations.length > 0 ? <div>Required migrations: {migrations.join(', ')}</div> : null}
                  {deps.length > 0 ? <div>Depends on: {deps.join(', ')}</div> : null}
                </div>
              ) : null}

              {warnings.length > 0 ? (
                <div
                  role="note"
                  style={{
                    padding: 'var(--space-3)',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--color-warning)',
                    background: 'var(--color-warning-ghost)',
                    color: 'var(--color-warning-lt)',
                    fontSize: 'var(--text-sm)',
                  }}
                >
                  <strong>Cannot enable yet:</strong>
                  <ul style={{ margin: 'var(--space-2) 0 0', paddingLeft: 'var(--space-4)' }}>
                    {warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              ) : null}

              {auditFor === flag.flag_key ? (
                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-steel-lt)' }}>
                  <strong>Audit history</strong>
                  {auditRows.length === 0 ? (
                    <div style={{ fontStyle: 'italic', color: 'var(--color-muted)' }}>No state changes recorded yet</div>
                  ) : (
                    <ul style={{ margin: 'var(--space-2) 0 0', paddingLeft: 'var(--space-4)' }}>
                      {auditRows.map((r) => (
                        <li key={r.id}>
                          {r.action} — {r.actor_email || 'system'} — {r.created_at}
                          {r.reason ? ` (${r.reason})` : ''}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : null}
            </div>
          );
        })
      )}
    </div>
  );
}
